import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useGoogleLogin } from "@react-oauth/google";

// Formik Validation
import * as Yup from "yup";
import { useFormik } from "formik";

import withRouter from "../../components/Common/withRouter";

// Redux
import { useDispatch } from "react-redux";
import { loginSuccess } from "../../store/auth/login/actions";

// API
import { post } from "../../api/axios";
import endpoints from "../../api/endpoints";

// Utils
import { extractErrorMessage } from "../../utils/errorHandler";
import { useRecaptcha, loadRecaptchaScript } from "../../utils/recaptcha";

// Config
import config from "../../config";

// Toast notifications
import toastr from "toastr";
import "toastr/build/toastr.min.css";

// reusable components
import CustomInput from "../../components/AuthenticationComponents/CustomInput";
import CustomButton from "../../components/AuthenticationComponents/CustomButton";
import logo from "../../assets/images/authentication/logo-auth.svg";
import "../../assets/scss/custom/pages/Authentication.scss";
import zapIcon from "../../assets/images/authentication/zap.svg";
import creditCardIcon from "../../assets/images/authentication/credit-card.svg";
import giftIcon from "../../assets/images/authentication/gift.svg";
import googleIcon from "../../assets/images/authentication/google-icon.svg";
import arrowRightIcon from "../../assets/images/authentication/arrow-right.svg";

// Detect environment based on hosting URL
const getEnvironmentConfig = () => {
  const hostname = window.location.hostname;

  // Production app URL: hide portal toggle, default to customer
  if (hostname === config.portal.customerUrl) {
    return {
      showPortalToggle: false,
      defaultPortal: "customer",
      filterPortals: null,
    };
  }

  // Production admin URL: show portal toggle, exclude customer option
  if (hostname === config.portal.adminUrl) {
    return {
      showPortalToggle: true,
      defaultPortal: "restaurant",
      filterPortals: ["restaurant", "admin"],
    };
  }

  // Local development: show all portals (current behavior)
  return {
    showPortalToggle: true,
    defaultPortal: "customer",
    filterPortals: null,
  };
};

// Portal config: drives the toggle, the API endpoint used, and the copy shown.
// NOTE: add these two keys to your endpoints.js if they aren't there yet:
//   adminLogin: "/api/login",
//   restaurantLogin: "/api/restaurants/login",
const PORTALS = {
  customer: {
    label: "Customer",
    endpointKey: "login",
    heading: "Welcome back",
    subheading: "Sign in to your Omaha Food Lovers account",
    showGoogle: true,
    showForgotPassword: true,
    forgotPasswordLink: "/forgot-password",
    bottomLink: {
      show: true,
      to: "/register",
      prefix: "Don't have an account?",
      text: "Sign up",
    },
  },
  restaurant: {
    label: "Restaurant",
    endpointKey: "restaurantLogin",
    heading: "Restaurant Login",
    subheading: "Sign in to manage your restaurant account",
    showGoogle: false,
    showForgotPassword: true,
    forgotPasswordLink: "/restaurant/forgot-password",
    bottomLink: {
      show: true,
      to: "/restaurant/redeem-voucher",
      prefix: "Want to redeem rewards?",
      text: "Redeem",
    },
  },
  admin: {
    label: "Admin",
    endpointKey: "adminLogin",
    heading: "Admin Login",
    subheading: "Sign in to the admin control panel",
    showGoogle: false,
    showForgotPassword: true,
    forgotPasswordLink: "/admin/forgot-password",
    bottomLink: { show: false, to: "", prefix: "", text: "" },
  },
};

const Login = (props) => {
  document.title = "Login | Omaha Food Lovers";

  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  // Get environment configuration based on hostname
  const envConfig = getEnvironmentConfig();

  // Configure toastr options
  toastr.options = {
    positionClass: "toast-bottom-left",
    timeOut: 5000,
    closeButton: true,
    progressBar: true,
  };

  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState("");
  const {
    getToken,
    executing: captchaExecuting,
    error: captchaError,
  } = useRecaptcha();
  const [portal, setPortal] = useState(() => {
    // If portal toggle is disabled, use default portal from env config
    if (!envConfig.showPortalToggle) {
      return envConfig.defaultPortal;
    }
    const portalParam = searchParams.get("portal");
    // Validate portal param against available portals
    const availablePortals = envConfig.filterPortals || [
      "customer",
      "restaurant",
      "admin",
    ];
    return portalParam && availablePortals.includes(portalParam)
      ? portalParam
      : envConfig.defaultPortal;
  }); // "customer" | "restaurant" | "admin"

  const activePortal = PORTALS[portal];

  const handlePortalChange = (key) => {
    if (key === portal) return;
    setPortal(key);
    setError("");
    validation.resetForm();
  };

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .trim()
        .email("Please enter a valid email")
        .required("Please enter your email")
        .max(255, "Email cannot exceed 255 characters")
        .matches(
          /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          "Please enter a valid email format",
        ),
      password: Yup.string()
        .trim()
        .required("Please enter your password")
        .max(128, "Password cannot exceed 128 characters"),
    }),
    onSubmit: async (values) => {
      setError("");

      // Fetch a fresh reCAPTCHA v3 token right before the API call
      const recaptchaToken = await getToken("login");
      if (!recaptchaToken) return; // captchaError is already set

      setLoading(true);
      try {
        const endpoint = endpoints[activePortal.endpointKey];

        const response = await post(endpoint, {
          email: values.email,
          password: values.password,
          recaptcha_token: recaptchaToken,
        });

        // Handle different response structures for customer vs restaurant/admin APIs
        // Customer API: response.data.success + response.data.data
        // Restaurant/Admin API: response.data.status + response.data.user
        const isSuccess = response.data?.success || response.data?.status;
        const userData = response.data?.data || response.data?.user;
        const token = userData?.token;

        if (isSuccess && userData && token) {
          // Store token in localStorage
          localStorage.setItem("accessToken", token);
          localStorage.setItem("user", JSON.stringify(userData));

          // Store in Redux
          dispatch(loginSuccess(userData));

          if (portal === "customer") {
            // Check if email is verified (customer portal only)
            if (!userData.email_verified_at) {
              toastr.info(
                response.data?.message ||
                  response.message ||
                  "Please verify your email address to continue.",
              );
              props.router.navigate("/email-verify");
            } else {
              toastr.success(
                response.data?.message ||
                  response.message ||
                  "Login successful!",
              );
              props.router.navigate("/dashboard");
            }
          } else if (portal === "restaurant") {
            // Restaurant portal goes to restaurant dashboard
            toastr.success(
              response.data?.message || response.message || "Login successful!",
            );
            props.router.navigate("/restaurant/dashboard");
          } else if (portal === "admin") {
            // Admin portal goes to admin dashboard
            toastr.success(
              response.data?.message || response.message || "Login successful!",
            );
            props.router.navigate("/admin/dashboard");
          }
        } else {
          setError(
            response.data?.message || response.message || "Login failed",
          );
          toastr.error(
            response.data?.message || response.message || "Login failed",
          );
        }
      } catch (error) {
        const errorMessage = extractErrorMessage(error);
        setError(errorMessage);
        toastr.error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    document.body.className = "authentication-bg";
    window.scrollTo(0, 0);
    // Preload reCAPTCHA so the v3 badge is visible and submit is instant
    loadRecaptchaScript().catch(() => {});

    return function cleanup() {
      document.body.className = "";
    };
  }, []);

  const googleLogin = useGoogleLogin({
    onSuccess: async (credentialResponse) => {
      setGoogleLoading(true);
      setError("");
      try {
        // Validate the credential response
        if (!credentialResponse?.credential) {
          throw new Error("Invalid Google credential response");
        }

        // Send the Google ID token to backend for authentication
        const response = await post(endpoints.googleLogin, {
          id_token: credentialResponse.credential,
        });

        if (response.data.success) {
          // Store token in localStorage
          localStorage.setItem("accessToken", response.data.data.token);
          localStorage.setItem("user", JSON.stringify(response.data.data));

          // Store in Redux
          dispatch(loginSuccess(response.data.data));

          // Check if email is verified
          if (!response.data.data.email_verified_at) {
            // Email not verified, redirect to email verification screen
            toastr.info(
              response.data.message ||
                "Please verify your email address to continue.",
            );
            props.router.navigate("/email-verify");
          } else {
            // Email verified, redirect to dashboard
            toastr.success(response.data.message || "Login successful!");
            props.router.navigate("/dashboard");
          }
        } else {
          setError(response.data.message || "Google login failed");
          toastr.error(response.data.message || "Google login failed");
        }
      } catch (error) {
        const errorMessage = extractErrorMessage(error);
        setError(errorMessage);
        toastr.error(errorMessage);
      } finally {
        setGoogleLoading(false);
      }
    },
    onError: (error) => {
      console.error("Google Login Error:", error);
      setError("Google login failed. Please try again.");
      toastr.error("Google login failed. Please try again.");
    },
  });

  return (
    <React.Fragment>
      <div className="auth-page">
        <div className="auth-page-grid">
          {/* left brand / marketing panel */}
          <div className="auth-brand-panel">
            <div className="auth-brand-panel-inner">
              <Link to="/" className="auth-logo">
                <img
                  className="auth-logo-icon"
                  src={logo}
                  alt="Omaha Food Lovers"
                />
              </Link>

              <h1 className="auth-headline">
                {portal === "restaurant" ? (
                  <>
                    Restaurant Portal
                    <br />
                    <span className="auth-headline-accent">
                      Omaha Food Lovers
                    </span>
                  </>
                ) : portal === "admin" ? (
                  <>
                    Admin Portal
                    <br />
                    <span className="auth-headline-accent">
                      Omaha Food Lovers
                    </span>
                  </>
                ) : (
                  <>
                    Earn rewards every time
                    <br />
                    you dine in{" "}
                    <span className="auth-headline-accent">Omaha</span>
                  </>
                )}
              </h1>

              <p className="auth-subline">
                {portal === "restaurant" ? (
                  <>
                    Manage your restaurant account.
                    <br />
                    Track redemptions, view platform fees, and more.
                  </>
                ) : portal === "admin" ? (
                  <>
                    Manage the platform.
                    <br />
                    Oversee users, restaurants, and system settings.
                  </>
                ) : (
                  <>
                    Get 5% back at 200+ restaurants.
                    <br />
                    Track your dining streak. Unlock exclusive perks.
                  </>
                )}
              </p>

              <ul className="auth-feature-list">
                {portal === "restaurant" ? (
                  <>
                    <li>
                      <span className="auth-feature-icon" aria-hidden="true">
                        <img src={creditCardIcon} alt="Credit Card" />
                      </span>
                      <span>
                        Track voucher redemptions, customer activity, and reward
                        usage in one place.
                      </span>
                    </li>
                    <li>
                      <span className="auth-feature-icon" aria-hidden="true">
                        <img src={giftIcon} alt="Gift" />
                      </span>
                      <span>
                        Manage your restaurant profile, business details, menu
                        information, and account settings.
                      </span>
                    </li>
                    <li>
                      <span className="auth-feature-icon" aria-hidden="true">
                        <img src={zapIcon} alt="Zap" />
                      </span>
                      <span>
                        Access detailed platform analytics, settlement reports,
                        payout history, and performance insights.
                      </span>
                    </li>
                  </>
                ) : portal === "admin" ? (
                  <>
                    <li>
                      <span className="auth-feature-icon" aria-hidden="true">
                        <img src={creditCardIcon} alt="Credit Card" />
                      </span>
                      <span>
                        Manage user accounts, roles, permissions, and access
                        across the entire platform.
                      </span>
                    </li>
                    <li>
                      <span className="auth-feature-icon" aria-hidden="true">
                        <img src={giftIcon} alt="Gift" />
                      </span>
                      <span>
                        Review restaurant registrations, verify applications,
                        and approve new business accounts.
                      </span>
                    </li>
                    <li>
                      <span className="auth-feature-icon" aria-hidden="true">
                        <img src={zapIcon} alt="Zap" />
                      </span>
                      <span>
                        Monitor platform performance, settlement reports,
                        transactions, and overall business activity.
                      </span>
                    </li>
                  </>
                ) : (
                  <>
                    <li>
                      <span className="auth-feature-icon" aria-hidden="true">
                        <img src={creditCardIcon} alt="Credit Card" />
                      </span>
                      <span>
                        Link your card once. Earn 5% back automatically at 200+
                        local restaurants.
                      </span>
                    </li>
                    <li>
                      <span className="auth-feature-icon" aria-hidden="true">
                        <img src={giftIcon} alt="Gift" />
                      </span>
                      <span>
                        Redeem points for restaurant vouchers. Your rewards stay
                        local, supporting Omaha's dining scene.
                      </span>
                    </li>
                    <li>
                      <span className="auth-feature-icon" aria-hidden="true">
                        <img src={zapIcon} alt="Zap" />
                      </span>
                      <span>
                        100% free. No subscription, no hidden fees. Just eat and
                        earn.
                      </span>
                    </li>
                  </>
                )}
              </ul>
            </div>
          </div>

          {/* right form panel */}
          <div className="auth-form-panel">
            <div className="auth-form-card">
              {/* Compact portal toggle: Customer / Restaurant / Admin (state-driven, no page change) */}
              {envConfig.showPortalToggle && (
                <div
                  className="auth-portal-toggle"
                  role="tablist"
                  aria-label="Choose portal"
                >
                  {Object.keys(PORTALS)
                    .filter(
                      (key) =>
                        !envConfig.filterPortals ||
                        envConfig.filterPortals.includes(key),
                    )
                    .map((key) => (
                      <button
                        type="button"
                        key={key}
                        className={
                          "auth-portal-toggle-option" +
                          (portal === key ? " active" : "")
                        }
                        role="tab"
                        aria-selected={portal === key}
                        onClick={() => handlePortalChange(key)}
                      >
                        {PORTALS[key].label}
                      </button>
                    ))}
                </div>
              )}

              <div className="auth-form-header">
                <h2>{activePortal.heading}</h2>
                <p>{activePortal.subheading}</p>
              </div>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  validation.handleSubmit();
                  return false;
                }}
              >
                <div className="custom-input-row">
                  <CustomInput
                    label="Email"
                    name="email"
                    type="email"
                    placeholder="alex@omaha.com"
                    value={validation.values.email}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    invalid={
                      validation.touched.email && !!validation.errors.email
                    }
                    errorText={validation.errors.email}
                    autoComplete="email"
                    className="glass"
                  />
                  <CustomInput
                    label="Password"
                    name="password"
                    type="password"
                    placeholder="••••••••"
                    value={validation.values.password}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    invalid={
                      validation.touched.password &&
                      !!validation.errors.password
                    }
                    errorText={validation.errors.password}
                    autoComplete="current-password"
                    className="glass"
                  />
                </div>
                {activePortal.showForgotPassword && (
                  <div className="auth-forgot-password">
                    <Link to={activePortal.forgotPasswordLink}>
                      Forgot password?
                    </Link>
                  </div>
                )}
                {error || captchaError ? (
                  <p className="auth-server-error">{error || captchaError}</p>
                ) : null}

                <div className="auth-submit-wrap">
                  <CustomButton
                    type="submit"
                    variant="primary"
                    iconPosition="right"
                    icon={arrowRightIcon}
                    loading={loading || captchaExecuting}
                  >
                    Log In
                  </CustomButton>
                </div>

                {activePortal.showGoogle && (
                  <>
                    <div className="auth-divider">
                      <span>Or</span>
                    </div>

                    <CustomButton
                      variant="secondary"
                      iconPosition="right"
                      onClick={googleLogin}
                      icon={googleIcon}
                      loading={googleLoading}
                    >
                      Continue with Google
                    </CustomButton>
                  </>
                )}

                {activePortal.bottomLink.show && (
                  <p className="auth-login-link">
                    {activePortal.bottomLink.prefix}{" "}
                    <Link to={activePortal.bottomLink.to}>
                      {activePortal.bottomLink.text}
                    </Link>
                  </p>
                )}
              </form>
            </div>
          </div>
        </div>
        <p className="auth-brand-footer">
          © Omaha Food Lovers {new Date().getFullYear()} - All Rights Reserved.
        </p>
      </div>
    </React.Fragment>
  );
};

export default withRouter(Login);
